import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { advanceReports, type AdvanceReportItem } from '@/api/advanceReports';
import { cashPayments } from '@/api/cashDocs';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  advance_payment_id: string;
  purpose: string;
  comment: string;
  project_id: string | null;
  items: AdvanceReportItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  employee_id: '',
  advance_payment_id: '',
  purpose: '',
  comment: '',
  project_id: null,
  items: [],
};

const newRow = (n: number, defaultVatId: string): AdvanceReportItem => ({
  line_number: n,
  description: '',
  expense_account_code: '92',
  amount_without_vat: '0',
  vat_rate_id: defaultVatId,
  vat_amount: '0',
  total_amount: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdvanceReportEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по cash.pko.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('cash.pko.manage');
  const costItemsQ = useQuery({ queryKey: ['cost-items'], queryFn: refs.costItems });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const docQuery = useQuery({
    queryKey: ['advance-report', id],
    queryFn: () => advanceReports.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'advance-report', (cid) => advanceReports.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  // Вкладка «Рахунки обліку» + дт-кт (рішення власника 2026-08-09) —
  // «Рах. витрат» перенесено з колонки таблиці сюди (єдиний вигляд).
  const [viewTab, setViewTab] = useState<'lines' | 'accounts'>('lines');
  const numberPreview = useNumberPreview('doc.advance_reports', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  const employeesQ = useQuery({
    queryKey: ['employees', form.organization_id],
    queryFn: () => refs.employees(form.organization_id),
    enabled: !!form.organization_id,
  });

  // РКО выдачи аванса этому сотруднику (operation_type=TO_PODOTCHET)
  const advancePaymentsQ = useQuery({
    queryKey: ['cash-payments-podotchet', form.organization_id, form.employee_id],
    queryFn: () => cashPayments.list({
      organization_id: form.organization_id,
      employee_id: form.employee_id,
      operation_type: 'TO_PODOTCHET',
      posted: true,
      limit: 100,
    }),
    enabled: !!form.organization_id && !!form.employee_id,
  });

  const defaultVatId = useMemo(() => {
    // Для подотчётов часто без НДС или 20%. Стартовое значение — пусто (без НДС).
    return '';
  }, []);

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1, defaultVatId)],
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        employee_id: d.employee_id,
        advance_payment_id: d.advance_payment_id ?? '',
        purpose: d.purpose ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          cost_item_id: it.cost_item_id,
          contractor_id: it.contractor_id,
          supplier_doc_number: it.supplier_doc_number,
          supplier_doc_date: it.supplier_doc_date,
          description: it.description,
          expense_account_code: it.expense_account_code,
          amount_without_vat: String(it.amount_without_vat),
          vat_rate_id: it.vat_rate_id,
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs, defaultVatId]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalWithoutVat = form.items.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0);
  const totalVat = form.items.reduce((s, e) => s + Number(e.vat_amount || 0), 0);
  const totalAmount = totalWithoutVat + totalVat;

  // Сумма выданного аванса: из текущего выбранного РКО (если уже выбран)
  // или из сохранённого docQuery.data.advance_received.
  const selectedPayment = advancePaymentsQ.data?.items.find(
    (p) => p.id === form.advance_payment_id,
  );
  const advanceReceived = selectedPayment
    ? Number(selectedPayment.amount)
    : Number(docQuery.data?.advance_received ?? 0);
  const balance = advanceReceived - totalAmount;

  const create = useMutation({
    mutationFn: () => advanceReports.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      employee_id: form.employee_id,
      advance_payment_id: form.advance_payment_id || null,
      purpose: form.purpose || null,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['advance-reports'] });
      navigate(`/advance-reports/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => advanceReports.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      employee_id: form.employee_id,
      advance_payment_id: form.advance_payment_id || null,
      purpose: form.purpose || null,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['advance-report', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await advanceReports.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        employee_id: form.employee_id,
        advance_payment_id: form.advance_payment_id || null,
        purpose: form.purpose || null,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      });
      try {
        await advanceReports.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/advance-reports/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['advance-reports'] });
      navigate(`/advance-reports/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: спершу зберегти правки, потім провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await advanceReports.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        employee_id: form.employee_id,
        advance_payment_id: form.advance_payment_id || null,
        purpose: form.purpose || null,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      });
      await advanceReports.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advance-report', id] });
      qc.invalidateQueries({ queryKey: ['advance-reports'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => advanceReports.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advance-report', id] });
      qc.invalidateQueries({ queryKey: ['advance-reports'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => advanceReports.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advance-reports'] });
      navigate('/advance-reports');
    },
  });

  function setItem(i: number, patch: Partial<AdvanceReportItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        if ('amount_without_vat' in patch || 'vat_rate_id' in patch) {
          const awv = Number(next.amount_without_vat) || 0;
          const vat = vatList.data?.find((v) => v.id === next.vat_rate_id);
          const rate = vat?.rate_percent ?? 0;
          const vatAmt = (awv * rate) / 100;
          next.vat_amount = vatAmt.toFixed(2);
          next.total_amount = (awv + vatAmt).toFixed(2);
        }
        return next;
      }),
    }));
  }
  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1, defaultVatId)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має cash.pko.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати авансовий звіт у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/advance-reports')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.869 (новий дизайн, Закон #10): «ще дій» не було — права колонка без
  // «Повʼязаних дій». Прогрес — лише обовʼязкові (схема: організація, дата,
  // підзвітна особа; рядки — для проведення).
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'person', title: 'Підзвітна особа і аванс', required: 1,
      filled: form.employee_id ? 1 : 0 },
    { id: 'items', title: 'Витрати', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий авансовий звіт' : `АО: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/advance-reports')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/advance-reports/${id}/print`)}
          // ч.869: дт-кт та історія — у тулбарі/рейці (окремі кнопки біля ТЧ прибрано).
          entriesDoc={{ type: 'doc.advance_reports', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/advance-reports/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.advance_reports', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Організація *</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>
            № документа
            <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: '0.7rem' }}>
              &nbsp;(авто, якщо порожнє)
            </span>
          </label>
          <input
            value={form.document_number}
            placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        <ProjectField span={3}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Підзвітна особа і аванс">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Підзвітна особа *</label>
          <select value={form.employee_id}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {employeesQ.data?.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name}{emp.code ? ` — ${emp.code}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 8' }}>
          <label>Призначення (мета відрядження/витрат)</label>
          <input value={form.purpose}
                 onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>

      {!!form.employee_id && (
        <div className="field">
          <label>РКО видачі авансу (необов'язково)</label>
          <select value={form.advance_payment_id}
                  onChange={(e) => setForm({ ...form, advance_payment_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— (без зв'язки)</option>
            {advancePaymentsQ.data?.items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.document_number} від {new Date(p.document_date).toLocaleDateString('uk-UA')}
                {' — '}{Number(p.amount).toLocaleString('uk-UA',
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн
              </option>
            ))}
          </select>
        </div>
      )}

      {!!form.advance_payment_id && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem',
          padding: '0.75rem 1rem', background: 'var(--bg-soft-2)', borderRadius: 6,
          marginBottom: '0.75rem', fontSize: '0.9rem',
        }}>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Видано (за РКО)</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{fmt(advanceReceived)} грн</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Витрачено</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{fmt(totalAmount)} грн</div>
          </div>
          <div>
            {balance > 0 ? (
              <>
                <div style={{ color: 'var(--badge-red-fg)', fontSize: '0.8rem' }}>Залишок до повернення</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--badge-red-fg)' }}>
                  {fmt(balance)} грн
                </div>
              </>
            ) : balance < 0 ? (
              <>
                <div style={{ color: 'var(--badge-green-fg)', fontSize: '0.8rem' }}>Перевитрата (борг працівнику)</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--badge-green-fg)' }}>
                  {fmt(-balance)} грн
                </div>
              </>
            ) : (
              <>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Підсумок</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>0,00 грн</div>
              </>
            )}
          </div>
        </div>
      )}

      </DocBlock>

      <DocBlock index={3} title="Витрати"
        actions={!isPosted ? (
          <button className="primary" onClick={addRow}>+ додати рядок</button>
        ) : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={viewTab === 'accounts' ? 'active' : ''}
            onClick={() => setViewTab(viewTab === 'accounts' ? 'lines' : 'accounts')}>
            Рахунки обліку</button>
        </div>
      </div>
      {viewTab === 'accounts' && (
        <div style={{ marginTop: '0.5rem' }}>
          <TchTable>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th>Опис витрати</th>
                <th style={{ width: 200 }}
                    title="Рахунок витрат рядка (порожньо — авто: 92)">Рах. витрат</th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <tr key={i}>
                  <td>{it.description || '—'}</td>
                  <td>
                    <input value={it.expense_account_code ?? ''} maxLength={10}
                      onChange={(e) => setItem(i, { expense_account_code: e.target.value || null })}
                      disabled={isPosted}
                      placeholder="авто: 92"
                      style={{ width: '100%' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Порожнє поле — типовий рахунок 92 (адмінвитрати).
          </p>
        </div>
      )}
      {viewTab === 'lines' && (
      <TchTable style={{ marginTop: '0.5rem' }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Опис</th>
            <th style={{ width: 160 }}>Стаття витрат</th>
            <th style={{ width: 120 }}>Сума без ПДВ</th>
            <th style={{ width: 100 }}>ПДВ</th>
            <th style={{ width: 100 }}>Сума ПДВ</th>
            <th style={{ width: 120 }}>Усього</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                <input value={it.description ?? ''}
                       onChange={(e) => setItem(i, { description: e.target.value })}
                       disabled={isPosted}
                       style={{ width: '100%' }} />
              </td>
              <td>
                <select value={it.cost_item_id ?? ''}
                        onChange={(e) => setItem(i, { cost_item_id: e.target.value || null })}
                        disabled={isPosted}>
                  <option value="">—</option>
                  {costItemsQ.data?.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} — {c.code}</option>
                  ))}
                </select>
              </td>
              <td>
                <input type="number" step="0.01" value={it.amount_without_vat}
                       onChange={(e) => setItem(i, { amount_without_vat: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right' }} />
              </td>
              <td>
                <select value={it.vat_rate_id ?? ''}
                        onChange={(e) => setItem(i, { vat_rate_id: e.target.value || null })}
                        disabled={isPosted}>
                  <option value="">— (без ПДВ)</option>
                  {vatList.data?.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </td>
              <td style={{ textAlign: 'right' }}>{fmt(Number(it.vat_amount))}</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(Number(it.total_amount))}</td>
              <td>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger" style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} style={{ textAlign: 'right' }}>Усього:</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalWithoutVat)}</td>
            <td></td>
            <td style={{ textAlign: 'right' }}>{fmt(totalVat)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalAmount)}</td>
            <td></td>
          </tr>
        </tfoot>
      </TchTable>
      )}
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.advance_reports" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          <strong>Як працює:</strong> на кожен рядок при проведенні створюється проводка
          {' '}<strong>Дт &lt;рах. витрат&gt; — Кт 3721</strong> на суму без ПДВ.
          Якщо вказана ставка ПДВ — додатково{' '}
          <strong>Дт 6442 — Кт 3721</strong> на суму ПДВ (податковий кредит непідтверджений).
          Залишок підзвітної заборгованості видно в ОСВ за рахунком 3721 в розрізі співробітників.
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
